import { useRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';

import DrawerTaskPanel, { TASK_PANEL_EVENTS } from '@/components/DrawerTaskPanel';
import { RightContext } from '@/components/RightProvider/context';

import DelayedRender from '../DelayRender';
import DrawerCollapseButton from '../DrawerTaskPanel/DrawerCollapseButton';
import { Right } from './index';

export interface RightWithAuthProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  /**
   * 未登录时显示的内容（酷炫效果图）
   */
  unauthorizedContent?: React.ReactNode;

  /**
   * 是否显示滑动面板和折叠按钮
   * @default false
   */
  showDrawer?: boolean;
  /**
   * 滑动面板宽度（包括 padding）
   * @default 398 (382px 内容 + 16px padding)
   */
  drawerWidth?: number;
  emptyText?: React.ReactNode;
}

/**
 * 带登录状态判断的 Right 组件
 *
 * 功能：
 * - 未登录：显示酷炫效果图（unauthorizedContent）
 * - 已登录：显示正常的预览内容（children）
 * - 统一管理滑动面板和折叠按钮的显示
 * - 自动计算 marginRight（避免 RightContext 重渲染）
 *
 * 使用示例：
 * ```tsx
 * <RightWithAuth
 *   showDrawer={true}
 *   unauthorizedContent={<FaceSwapperPromo />}
 * >
 *   <VideoPreview />
 * </RightWithAuth>
 * ```
 */
const RightWithAuth: React.FC<RightWithAuthProps> = ({
  className,
  style,
  children,
  unauthorizedContent,
  showDrawer = false,
  emptyText,
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  // 使用组件内部 state 管理折叠状态，避免 RightContext 重渲染
  const [drawerCollapse, setDrawerCollapse] = useState(true);
  // Right 元素的 ref
  const rightRef = useRef<HTMLDivElement>(null);
  // 记录上一次的路由路径，用于检测路由切换
  const prevPathRef = useRef<string>('');
  // Right 的位置和尺寸信息
  const [rightBounds, setRightBounds] = useState<{
    top: number;
    right: number;
    bottom: number;
    height: number;
  } | null>(null);

  // 如果不需要 drawer，简化为仅做登录检查的简单模式
  const simpleMode = !showDrawer;

  // 使用 ref 保存最新的 rightContext 和 showDrawer，避免因依赖变化导致重新检查登录
  const rightContextRef = useRef(rightContext);
  rightContextRef.current = rightContext;

  const showDrawerRef = useRef(showDrawer);
  showDrawerRef.current = showDrawer;

  // 更新 Right 的位置和尺寸
  const updateRightBounds = () => {
    if (rightRef.current) {
      const rect = rightRef.current.getBoundingClientRect();
      setRightBounds({
        top: rect.top,
        right: window.innerWidth - rect.right,
        bottom: window.innerHeight - rect.bottom,
        height: rect.height,
      });
    }
  };

  // 登录检查和登录状态监听
  useEffect(() => {
    // 初始登录检查
    const checkLogin = async () => {
      setIsChecking(true);
      try {
        let loggedIn = false;

        // 使用默认的登录检查
        loggedIn = await rightContextRef.current.getUserLoginState({
          skipLoginModal: true,
        });

        setIsLoggedIn(loggedIn);

        // 如果用户已登录且显示任务面板，自动展开
        if (loggedIn && showDrawerRef.current) {
          setDrawerCollapse(true);
        }
      } finally {
        setIsChecking(false);
      }
    };

    // 监听登录成功事件
    const handleLoginSuccess = async () => {
      // 重新检查登录状态
      const loggedIn = await rightContextRef.current.getUserLoginState({
        skipLoginModal: true,
      });

      if (loggedIn) {
        setIsLoggedIn(true);

        // 自动展开任务面板
        if (showDrawerRef.current) {
          setDrawerCollapse(true);
        }
      }
    };

    // 执行初始检查
    checkLogin();

    // 添加登录成功事件监听
    window.addEventListener('userLoginSuccess', handleLoginSuccess);

    // 清理函数
    return () => {
      window.removeEventListener('userLoginSuccess', handleLoginSuccess);
    };
  }, []); // 只在 mount 时执行一次

  // 监听 Right 的尺寸和位置变化
  useEffect(() => {
    if (!showDrawer) return;

    // 初始化时更新一次
    updateRightBounds();

    // 监听窗口 resize
    window.addEventListener('resize', updateRightBounds);

    // 使用 ResizeObserver 监听 Right 元素的尺寸变化
    const resizeObserver = new ResizeObserver(() => {
      updateRightBounds();
    });

    if (rightRef.current) {
      resizeObserver.observe(rightRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateRightBounds);
      resizeObserver.disconnect();
    };
  }, [showDrawer]);

  // 监听任务面板展开事件
  useEffect(() => {
    const handleExpandPanel = () => {
      setDrawerCollapse(true);
    };

    window.addEventListener(TASK_PANEL_EVENTS.EXPAND_PANEL, handleExpandPanel);

    return () => {
      window.removeEventListener(TASK_PANEL_EVENTS.EXPAND_PANEL, handleExpandPanel);
    };
  }, []);

  // 监听路由变化，在进入有任务面板的页面时自动展开（如果用户已登录）
  useEffect(() => {
    const currentPath = router.pathname;

    // 如果路由发生了变化
    if (prevPathRef.current && prevPathRef.current !== currentPath) {
      // 如果新页面有任务面板且用户已登录，自动展开
      if (showDrawer && isLoggedIn && !drawerCollapse) {
        console.log('[RightWithAuth] Auto-expanding task panel on route change');
        setDrawerCollapse(true);
      }
    }

    // 更新上一次的路径
    prevPathRef.current = currentPath;
  }, [router.pathname, showDrawer, isLoggedIn, drawerCollapse]);

  // 简单模式：只渲染 children，跳过复杂的 drawer 逻辑
  if (simpleMode) {
    // 加载中
    if (isChecking) {
      return (
        <Right className={className} style={style}>
          <DelayedRender>
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-[#999]">Loading...</div>
            </div>
          </DelayedRender>
        </Right>
      );
    }

    // 未登录
    if (!isLoggedIn && unauthorizedContent) {
      return (
        <Right className={className} style={style}>
          {unauthorizedContent}
        </Right>
      );
    }

    // 已登录：直接返回 children，不需要 ref、bounds 等复杂逻辑
    return (
      <Right className={className} style={style}>
        {children}
      </Right>
    );
  }

  const computedStyle: React.CSSProperties = {
    ...style,
  };

  // Drawer 渲染函数 - 使用 Portal 渲染到 body，避免层级问题
  const renderDrawer = () => {
    if (!isLoggedIn || !showDrawer || !rightBounds) return null;

    const drawerContent = (
      <div className={`relative ${drawerCollapse ? 'ml-[16px]' : ''}`}>
        <DrawerTaskPanel collapse={drawerCollapse} emptyText={emptyText} />
        <DrawerCollapseButton onClick={() => setDrawerCollapse((prev) => !prev)} />
      </div>
    );

    return drawerContent;
  };

  // 加载中状态
  if (isChecking) {
    return (
      <>
        <Right ref={rightRef} className={className} style={computedStyle}>
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-[#999]">Loading...</div>
          </div>
        </Right>
        {renderDrawer()}
      </>
    );
  }

  // 未登录状态 - 不显示滑动面板和折叠按钮
  if (!isLoggedIn && unauthorizedContent) {
    return (
      <>
        <Right ref={rightRef} className={className} style={computedStyle}>
          {unauthorizedContent}
        </Right>
        {renderDrawer()}
      </>
    );
  }

  // 已登录状态
  return (
    <>
      <Right ref={rightRef} className={className} style={computedStyle}>
        {children}
      </Right>
      {renderDrawer()}
    </>
  );
};

export default RightWithAuth;
